import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fields = [
  { name: "name", label: "Your name", type: "text", placeholder: "Koffi Roland" },
  { name: "email", label: "Email address", type: "email", placeholder: "you@company.com" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Message sent.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-[#E5E5DF] bg-[#F9F9F7]">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5"
          >
            <span className="overline">Contact — Let&apos;s talk</span>
            <h2 className="mt-6 font-display text-5xl font-medium leading-[0.95] tracking-tight md:text-7xl">
              Looking for an <span className="italic text-[#5C5C5C]">apprentice</span> in data?
            </h2>
            <p className="mt-8 max-w-sm font-sans text-base leading-relaxed text-[#5C5C5C]">
              Tell me about your team and the problems you&apos;re solving. I&apos;ll get back to you as soon as I can.
            </p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-6 md:col-start-7"
          >
            <div className="flex flex-col gap-10">
              {fields.map((f) => (
                <label key={f.name} className="group block">
                  <span className="overline block mb-4">{f.label}</span>
                  <input
                    type={f.type}
                    name={f.name}
                    value={form[f.name]}
                    onChange={handleChange}
                    placeholder={f.placeholder}
                    data-testid={`contact-${f.name}`}
                    className="w-full bg-transparent border-b border-[#0A0A0A]/25 pb-3 font-display text-2xl md:text-3xl text-[#0A0A0A] placeholder:text-[#0A0A0A]/25 focus:border-[#0A0A0A] transition-colors duration-300"
                  />
                </label>
              ))}

              <label className="group block">
                <span className="overline block mb-4">Message</span>
                <textarea
                  name="message"
                  rows={3}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="What would you like to build together?"
                  data-testid="contact-message"
                  className="w-full resize-none bg-transparent border-b border-[#0A0A0A]/25 pb-3 font-display text-2xl md:text-3xl text-[#0A0A0A] placeholder:text-[#0A0A0A]/25 focus:border-[#0A0A0A] transition-colors duration-300"
                />
              </label>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group relative mt-4 self-start overflow-hidden border border-[#0A0A0A] px-10 py-4 font-mono text-[0.72rem] uppercase tracking-[0.2em] text-[#F9F9F7] disabled:opacity-60"
              >
                <span className="absolute inset-0 bg-[#0A0A0A] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-full" />
                <span className="absolute inset-0 -translate-y-full bg-[#0A0A0A] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
                <span className="relative transition-colors duration-500 group-hover:text-[#F9F9F7]">
                  {loading ? "Sending…" : "Send message"}
                </span>
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
